// Hörerseite, Wünsche und Player-Baustein aus Sicht der Redaktion.

import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { CopyValue, errorText } from "./streaming-ui";
import { Sym } from "./social-ui";
import { Badge } from "./ui";

type Requests = Awaited<ReturnType<typeof api.listRequests>>;

/** Offene Wünsche mit Annehmen/Ablehnen; darunter die zuletzt entschiedenen. */
export function RequestInbox({ slug, compact = false }: { slug: string; compact?: boolean }): JSX.Element | null {
  const [items, setItems] = useState<Requests | null>(null);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([api.listRequests(slug), api.getPublicSettings(slug)]);
      setItems(r);
      setEnabled(s.settings.requests !== "off");
    } catch (err) {
      setError(errorText(err));
    }
  }, [slug]);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 15_000);
    return () => clearInterval(t);
  }, [load]);

  if (!items || enabled === null) return null;
  const pending = items.filter((r) => r.status === "pending");
  if (compact && pending.length === 0) return null;
  if (!enabled && items.length === 0) return compact ? null : <p className="text-sm text-muted">Wünsche sind ausgeschaltet.</p>;

  const decide = async (id: string, accept: boolean): Promise<void> => {
    try {
      await (accept ? api.acceptRequest(id) : api.rejectRequest(id));
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="space-y-2">
      {error && <div className="callout callout-danger">{error}</div>}
      {pending.length === 0 ? (
        <p className="text-sm text-muted">Keine offenen Wünsche.</p>
      ) : (
        <ul className="space-y-1.5">
          {pending.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-sm">
              <Sym name="music" className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="font-medium text-primary">{[r.artist, r.title].filter(Boolean).join(" – ")}</span>
              {r.listener_name && <span className="text-xs text-secondary">von {r.listener_name}</span>}
              <span className="text-xs tabular-nums text-muted">{new Date(r.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span>
              {r.message && <span className="w-full pl-6 text-xs italic text-secondary">„{r.message}“</span>}
              <span className="flex-1" />
              <button onClick={() => void decide(r.id, true)} className="btn btn-primary btn-sm"><Sym name="check" className="h-3.5 w-3.5" />In den Sendeablauf</button>
              <button onClick={() => void decide(r.id, false)} className="btn btn-secondary btn-sm">Ablehnen</button>
            </li>
          ))}
        </ul>
      )}
      {!compact && items.some((r) => r.status !== "pending") && (
        <details className="text-xs text-muted">
          <summary className="cursor-pointer font-medium hover:text-primary">Heute entschieden ({items.filter((r) => r.status !== "pending").length})</summary>
          <ul className="mt-2 space-y-1 border-l-2 border-default pl-3">
            {items.filter((r) => r.status !== "pending").map((r) => (
              <li key={r.id}>{{ accepted: "✓ eingeplant", played: "♪ gespielt", rejected: "✗ abgelehnt", pending: "" }[r.status]} · {[r.artist, r.title].filter(Boolean).join(" – ")}{r.listener_name ? ` (${r.listener_name})` : ""}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

type Messages = Awaited<ReturnType<typeof api.listMessages>>;

const clock = (iso: string): string => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

/**
 * Nachrichten ins Studio. Hörer können nur schreiben, solange hier (oder im Studio) freigegeben ist.
 * compact: nur ungelesene, und nur wenn es welche gibt.
 */
export function MessageInbox({ slug, canModerate, compact = false }: { slug: string; canModerate: boolean; compact?: boolean }): JSX.Element | null {
  const [items, setItems] = useState<Messages | null>(null);
  const [open, setOpen] = useState<Awaited<ReturnType<typeof api.getMessagesOpen>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Grußwand an? Nur dann gibt es den Knopf „Auf die Grußwand“. */
  const [wallOn, setWallOn] = useState(false);

  useEffect(() => {
    api.listModules()
      .then((mods) => setWallOn(mods.some((m) => m.id === "greetings" && m.enabled)))
      .catch(() => setWallOn(false));
  }, []);

  const load = useCallback(async () => {
    try {
      const [m, o] = await Promise.all([api.listMessages(slug), api.getMessagesOpen(slug)]);
      setItems(m);
      setOpen(o);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [slug]);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [load]);

  if (!items || !open) return compact ? null : <div className="text-sm text-muted">{error ?? "Lade …"}</div>;
  const unread = items.filter((m) => m.status === "new");
  if (compact && unread.length === 0) return null;
  const shown = compact ? unread : items;

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="space-y-2">
      {error && <div className="callout callout-danger">{error}</div>}
      {!compact && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {open.open ? (
            <>
              <Badge tone="success" dot>Nachrichten an – bis {clock(open.open_until!)} Uhr{open.opened_by ? ` (${open.opened_by})` : ""}</Badge>
              {canModerate && <button onClick={() => void run(() => api.setMessagesOpen(slug, false))} className="btn btn-secondary btn-sm">Ausschalten</button>}
            </>
          ) : (
            <>
              <span className="text-muted">Hörer können gerade keine Nachrichten schicken.</span>
              {canModerate && <button onClick={() => void run(() => api.setMessagesOpen(slug, true, 60))} className="btn btn-primary btn-sm">Nachrichten annehmen (60 Min.)</button>}
            </>
          )}
        </div>
      )}
      {!compact && <p className="text-xs text-muted">Das Studio schaltet das während einer Live-Sendung von selbst ein und danach wieder aus.</p>}
      {shown.length === 0 ? (
        <p className="text-sm text-muted">Keine Nachrichten.</p>
      ) : (
        <ul className="space-y-1.5">
          {shown.map((m) => (
            <li key={m.id} className={`rounded-lg border px-3 py-2 text-sm ${m.status === "new" ? "border-sky-500/30 bg-sky-500/[0.06] text-primary" : "border-default text-secondary"}`}>
              <div className="flex flex-wrap items-center gap-2">
                {m.status === "new" && <span className="h-2 w-2 rounded-full bg-sky-500" aria-label="neu" />}
                <span className="font-medium text-primary">{m.listener_name}{m.place ? ` aus ${m.place}` : ""}</span>
                <span className="text-xs tabular-nums text-muted">{clock(m.created_at)}</span>
                {m.kind === "voice" && <Badge tone="info"><Sym name="mic" className="h-3 w-3" />Sprachnachricht · {Math.round((m.duration_ms ?? 0) / 1000)} s</Badge>}
                {m.status === "aired" && <Badge>{m.kind === "voice" ? "gesendet" : "vorgelesen"}</Badge>}
                {wallOn && m.wall === "on" && <Badge tone="success">auf der Grußwand</Badge>}
                <span className="flex-1" />
                {canModerate && (
                  <>
                    {m.status === "new" && <button onClick={() => void run(() => api.setMessageStatus(m.id, "read"))} className="btn btn-ghost btn-sm h-7 px-2">Gelesen</button>}
                    {m.status !== "aired" && <button onClick={() => void run(() => api.setMessageStatus(m.id, "aired"))} className="btn btn-ghost btn-sm h-7 px-2">{m.kind === "voice" ? "Gesendet" : "Vorgelesen"}</button>}
                    <button onClick={() => void run(() => api.setMessageStatus(m.id, "hidden"))} className="btn btn-ghost btn-sm h-7 px-2" title="Aus der Liste nehmen">Ausblenden</button>
                    {wallOn && m.kind === "text" && (
                      m.wall === "on" ? (
                        <button onClick={() => void run(() => api.setMessageWall(m.id, false))}
                          className="btn btn-ghost btn-sm h-7 px-2" title="Von der Grußwand nehmen – der Gruß verschwindet sofort">Von der Wand</button>
                      ) : (
                        <button onClick={() => void run(() => api.setMessageWall(m.id, true))}
                          className="btn btn-ghost btn-sm h-7 px-2" title="Öffentlich auf der Hörerseite zeigen">Auf die Grußwand</button>
                      )
                    )}
                    <button onClick={() => window.confirm("Nachrichten von diesem Gerät bis heute Nacht nicht mehr annehmen?") && void run(() => api.blockMessageSender(m.id))}
                      className="btn btn-ghost btn-sm h-7 px-2 !text-red-600 dark:!text-red-400" title="Nachricht ausblenden und das Gerät für heute sperren">Sperren</button>
                  </>
                )}
              </div>
              {m.kind === "voice" ? <VoicePlayer id={m.id} /> : <div className="mt-1 whitespace-pre-wrap break-words leading-relaxed">{m.message}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Sprachnachricht anhören. Lädt erst beim Klick – sonst holt die Liste alle Aufnahmen auf einmal. */
function VoicePlayer({ id }: { id: string }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (url) return <audio className="mt-1 w-full" src={url} controls autoPlay />;
  return (
    <div className="mt-1 flex items-center gap-2">
      <button onClick={() => api.messageAudioUrl(id).then(setUrl).catch((err) => setError(errorText(err)))} className="btn btn-secondary btn-sm">
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sky-600 text-white"><Sym name="play" className="h-2 w-2" /></span>Anhören
      </button>
      <span className="text-xs text-muted">Zum Senden im Studio einspielen.</span>
      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
    </div>
  );
}

/** Einstellungen für die Hörerseite: Wünsche, Verlauf, Player zum Einbauen. */
export function PublicSettingsPanel({ slug }: { slug: string }): JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getPublicSettings>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [size, setSize] = useState<"klein" | "breit">("breit");
  useEffect(() => {
    api.getPublicSettings(slug).then(setData).catch((err) => setError(errorText(err)));
  }, [slug]);
  if (!data) return <div className="text-sm text-muted">{error ?? "Lade …"}</div>;
  const save = async (patch: { requests?: "off" | "moderated" | "auto"; show_history?: boolean }): Promise<void> => {
    try {
      const r = await api.savePublicSettings(slug, patch);
      setData({ ...data, settings: r.settings });
    } catch (err) {
      setError(errorText(err));
    }
  };
  const iframe = size === "breit"
    ? `<iframe src="${data.embed_url}" width="100%" height="120" style="border:0;max-width:640px" title="Radio hören" allow="autoplay"></iframe>`
    : `<iframe src="${data.embed_url}" width="320" height="120" style="border:0" title="Radio hören" allow="autoplay"></iframe>`;
  return (
    <div className="space-y-5">
      {error && <div className="callout callout-danger">{error}</div>}
      <div className="space-y-1.5">
        <div className="field-label mb-0">Hörerseite</div>
        <a href={data.listen_page} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-center gap-1.5 text-sm font-medium text-brand-primary underline-offset-2 hover:underline">
          <Sym name="external" className="h-3.5 w-3.5" /><span className="truncate">{data.listen_page}</span>
        </a>
      </div>
      <div className="space-y-1.5">
        <div className="field-label mb-0">Musikwünsche</div>
        <div className="tabbar" role="radiogroup" aria-label="Musikwünsche">
          {([["off", "Aus"], ["moderated", "Nach Freigabe"], ["auto", "Automatisch einplanen"]] as const).map(([value, label]) => (
            <button key={value} onClick={() => void save({ requests: value })} role="radio" aria-checked={data.settings.requests === value}
              className={`tab ${data.settings.requests === value ? "tab-active" : ""}`}>
              {label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted">
          Hörer suchen auf der Hörerseite einen Titel aus der Mediathek. Was gerade lief oder bald kommt, lässt sich nicht wünschen;
          jeder kann höchstens drei Wünsche pro Stunde schicken. „Automatisch“ setzt den Wunsch hinter den nächsten geplanten Titel.
        </p>
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-primary">
        <input type="checkbox" className="h-4 w-4" checked={data.settings.show_history} onChange={(e) => void save({ show_history: e.target.checked })} />
        „Zuletzt gespielt“ auf der Hörerseite zeigen
      </label>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-[0.8rem] font-medium text-secondary">
          Player für die eigene Website
          <select value={size} onChange={(e) => setSize(e.target.value as "klein" | "breit")} className="input h-7 w-auto py-0 text-xs">
            <option value="breit">breit</option>
            <option value="klein">klein</option>
          </select>
        </div>
        <CopyValue label="Code" value={iframe} />
        <p className="text-xs text-muted">In die eigene Website einfügen (z. B. WordPress-Block „Individuelles HTML“). Mit <code>?theme=dark</code> hinter der Adresse wird er dunkel.</p>
      </div>
    </div>
  );
}
